import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, jsonBody } from '../../lib/api-client';
import type {
  ReferralActivity,
  ReferralLeaderboardEntry,
  ReferralStats,
} from '../../lib/api-types';

export const referralKeys = {
  all: ['account', 'referrals'] as const,
  code: ['account', 'referrals', 'code'] as const,
  history: ['account', 'referrals', 'history'] as const,
  leaderboard: ['account', 'referrals', 'leaderboard'] as const,
  stats: ['account', 'referrals', 'stats'] as const,
};

export async function fetchReferralCode(): Promise<string> {
  return (await apiRequest<{ code: string }>('/api/referrals/code')).code;
}

export async function fetchReferralStats(): Promise<ReferralStats> {
  return (await apiRequest<{ stats: ReferralStats }>('/api/referrals/stats')).stats;
}

export async function fetchReferralHistory(): Promise<ReferralActivity[]> {
  return (await apiRequest<{ history: ReferralActivity[] }>('/api/referrals/history')).history;
}

export async function fetchReferralLeaderboard(): Promise<ReferralLeaderboardEntry[]> {
  return (
    await apiRequest<{ leaderboard: ReferralLeaderboardEntry[] }>('/api/referrals/leaderboard')
  ).leaderboard;
}

export async function registerReferralCode(referralCode: string): Promise<void> {
  return apiRequest('/api/referrals/register', {
    body: jsonBody({ referralCode }),
    method: 'POST',
  });
}

export async function validateReferralCode(code: string): Promise<boolean> {
  return (await apiRequest<{ valid: boolean }>(`/api/referrals/validate/${code}`)).valid;
}

export function useReferralCode() {
  return useQuery({ queryFn: fetchReferralCode, queryKey: referralKeys.code });
}

export function useReferralStats() {
  return useQuery({ queryFn: fetchReferralStats, queryKey: referralKeys.stats });
}

export function useReferralHistory() {
  return useQuery({ queryFn: fetchReferralHistory, queryKey: referralKeys.history });
}

export function useReferralLeaderboard() {
  return useQuery({ queryFn: fetchReferralLeaderboard, queryKey: referralKeys.leaderboard });
}

export function useRegisterReferral() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: registerReferralCode,
    onSuccess: async () => client.invalidateQueries({ queryKey: referralKeys.all }),
  });
}
